// XLKpiCard - Sprint 44 Fase 3 Vista 1.
// Card de KPI para el dashboard: label + valor grande (mono) + hint + accent.
// Usa tokens XL (CSS vars) + dark mode + skeleton en loading. Sin estilos
// inline. Accent mapea a los colores semanticos ya disponibles en tokens.
//
// Uso:
//   <XLKpiCard label="Inventario valorizado" value="COP 15.400.000"
//              hint="Costo promedio NIIF" accent="success" />
//   <XLKpiCard label="POs pendientes" value="12" accent="warning" loading={isLoading} />
import { useEffect } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import XLSkeleton from '../../../components/xl/XLSkeleton';

const CSS_ID = 'xl-kpi-card-css';
const CSS = `
.xl-kpi {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  padding: 16px 18px;
  box-shadow: var(--xl-shadow-sm);
  font-family: var(--xl-font-sans);
  transition: border-color 120ms cubic-bezier(0,0,0.2,1),
              box-shadow   120ms cubic-bezier(0,0,0.2,1);
  min-width: 0;
}
.xl-kpi:hover {
  border-color: rgba(46, 55, 69, 0.25);
  box-shadow: var(--xl-shadow-md);
}
.xl-kpi__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--xl-color-text-muted);
  line-height: 1.2;
}
.xl-kpi__dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--xl-color-text-muted);
  flex-shrink: 0;
}
.xl-kpi--info    .xl-kpi__dot { background: var(--xl-color-info) }
.xl-kpi--success .xl-kpi__dot { background: var(--xl-color-success) }
.xl-kpi--warning .xl-kpi__dot { background: var(--xl-color-warning) }
.xl-kpi--danger  .xl-kpi__dot { background: var(--xl-color-danger) }
.xl-kpi--indigo  .xl-kpi__dot { background: #6366F1 }

.xl-kpi__value {
  font-family: var(--xl-font-mono);
  font-weight: 600;
  font-size: 22px;
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: var(--xl-color-text);
  min-height: 26px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.xl-kpi--info    .xl-kpi__value { color: var(--xl-color-info) }
.xl-kpi--success .xl-kpi__value { color: var(--xl-color-success) }
.xl-kpi--warning .xl-kpi__value { color: var(--xl-color-warning) }
.xl-kpi--danger  .xl-kpi__value { color: var(--xl-color-danger) }
.xl-kpi--indigo  .xl-kpi__value { color: #6366F1 }
[data-xl-theme="dark"] .xl-kpi--indigo .xl-kpi__value { color: #A5B4FC }

.xl-kpi__hint {
  font-size: 11px;
  color: var(--xl-color-text-muted);
  line-height: 1.3;
}
`;

const VALID_ACCENTS = new Set(['neutral', 'info', 'success', 'warning', 'danger', 'indigo']);

export default function XLKpiCard({
  label,
  value,
  hint,
  accent = 'neutral',
  loading = false,
  className = '',
}) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const a = VALID_ACCENTS.has(accent) ? accent : 'neutral';
  const cls = [
    'xl-kpi',
    a !== 'neutral' ? 'xl-kpi--' + a : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      <div className="xl-kpi__label">
        <span className="xl-kpi__dot" aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className="xl-kpi__value" aria-live="polite">
        {loading ? <XLSkeleton variant="line" width="60%" height={20} /> : value}
      </div>
      {hint && (
        <div className="xl-kpi__hint">
          {loading ? <XLSkeleton variant="line" width="40%" height={10} /> : hint}
        </div>
      )}
    </div>
  );
}
